import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";
import Logo from "../Assest/images/logo.png";
import { Eye, EyeOff, Lock, RefreshCcw } from "lucide-react";

const Signup = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordErrors, setPasswordErrors] = useState([]);

  const navigate = useNavigate();

  // Password validation function
  const validatePassword = (password) => {
    const errors = [];
    const strengths = [];

    // Check length
    if (password.length < 8) {
      errors.push("Password must be at least 8 characters long");
    } else {
      strengths.push("Length requirement met");
    }

    // Check for uppercase
    if (!/[A-Z]/.test(password)) {
      errors.push("Must contain at least one uppercase letter");
    } else {
      strengths.push("Uppercase letter included");
    }

    // Check for lowercase
    if (!/[a-z]/.test(password)) {
      errors.push("Must contain at least one lowercase letter");
    } else {
      strengths.push("Lowercase letter included");
    }

    // Check for number
    if (!/[0-9]/.test(password)) {
      errors.push("Must contain at least one number");
    } else {
      strengths.push("Number included");
    }

    // Check for special character
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push("Must contain at least one special character");
    } else {
      strengths.push("Special character included");
    }

    // Calculate password strength
    const strength = strengths.length;
    setPasswordStrength(strength);
    setPasswordErrors(errors);

    return errors.length === 0;
  };

  // Generate strong password
  const generatePassword = () => {
    const uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const lowercase = "abcdefghijklmnopqrstuvwxyz";
    const numbers = "0123456789";
    const symbols = '!@#$%^&*(),.?":{}|<>';

    const getRandomChar = (charset) =>
      charset[Math.floor(Math.random() * charset.length)];

    let password = "";
    // Ensure at least one of each required character type
    password += getRandomChar(uppercase);
    password += getRandomChar(lowercase);
    password += getRandomChar(numbers);
    password += getRandomChar(symbols);

    // Fill the rest of the password
    while (password.length < 12) {
      const allChars = uppercase + lowercase + numbers + symbols;
      password += getRandomChar(allChars);
    }

    // Shuffle the password
    const passwordArray = password.split("");
    for (let i = passwordArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [passwordArray[i], passwordArray[j]] = [
        passwordArray[j],
        passwordArray[i],
      ];
    }

    const generatedPassword = passwordArray.join("");

    // Update form data and validate
    setFormData((prev) => ({
      ...prev,
      password: generatedPassword,
      confirmPassword: generatedPassword,
    }));
    validatePassword(generatedPassword);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Special validation for password
    if (name === "password") {
      validatePassword(value);
    }
  };
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);

    // Validate all fields
    if (
      !formData.firstName ||
      !formData.lastName ||
      !formData.email ||
      !formData.phone ||
      !formData.password ||
      !formData.confirmPassword
    ) {
      toast.error("Please fill in all fields");
      setLoading(false);
      return;
    }

    // Validate password match
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      setLoading(false);
      return;
    }

    // ✅ Call validatePassword directly and check its return value
    const isPasswordValid = validatePassword(formData.password);
    if (!isPasswordValid) {
      toast.error("Please correct password errors");
      setLoading(false);
      return;
    }

    // Validate terms
    if (!agreeTerms) {
      toast.error("Please agree to the Terms of Service and Privacy Policy");
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URI}/auth/signup`,
        {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          password: formData.password,
          roleId: 5,
          companyId: 65,
        }
      );

      if (response.status === 201) {
        toast.success(
          "Signup successful!"
        );
        setTimeout(() => {
          navigate("/login");
        }, 1500);
      }
    } catch (error) {
      toast.error(
        "Signup failed: " +
        (error.response?.data?.message || "Please try again")
      );
      console.error("Signup error", error);
    } finally {
      setLoading(false); // Stop loading once request is complete
    }
  };

  return (
    <div className="min-h-screen flex bg-gray-100">
      {/* Left side banner (remains the same as previous code) */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#f36812] via-[#ff8a43] to-[#ff9d5c] p-12 flex-col justify-between relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full">
            <svg viewBox="0 0 1000 1000" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="b" gradientTransform="rotate(45 0.5 0.5)">
                  <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.7" />
                  <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path
                d="M0,1000 C200,800 400,900 500,800 C600,700 700,800 1000,900 L1000,1000 L0,1000 Z"
                fill="url(#b)"
              />
              <path
                d="M0,1000 C300,900 400,850 500,950 C600,1050 800,900 1000,1000 L0,1000 Z"
                fill="rgba(255,255,255,0.1)"
              />
            </svg>
          </div>
        </div>

        <div className="relative z-10">
          {/* White background for logo to make it visible */}
          <div className="brightness-0 invert mb-10">
            <Link to="/" className="brightness-0 invert mb-10 inline-block">
              <img src={Logo} alt="vital Source" className="h-12" />
            </Link>
          </div>
          <h1 className="text-white text-5xl font-bold leading-tight">
            Shop with <br />
            vital Source.
          </h1>
          <p className="text-white/90 mt-6 text-lg max-w-md">
            Join our shopping community today and discover a world of exclusive
            deals, seasonal collections, and personalized shopping experiences.
          </p>
        </div>

        <div className="relative z-10 flex flex-col">
          <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20 mb-10">
            <div className="flex items-center mb-4">
              <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center mr-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-white"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-white font-semibold">Free Shipping</h3>
                <p className="text-white/70 text-sm">On orders over $50</p>
              </div>
            </div>
            <div className="flex items-center">
              <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center mr-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-white"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM14 11a1 1 0 011 1v1h1a1 1 0 110 2h-1v1a1 1 0 11-2 0v-1h-1a1 1 0 110-2h1v-1a1 1 0 011-1z" />
                </svg>
              </div>
              <div>
                <h3 className="text-white font-semibold">Member Discounts</h3>
                <p className="text-white/70 text-sm">
                  Early access to sales & promotions
                </p>
              </div>
            </div>
          </div>

          <svg
            className="w-full"
            viewBox="0 0 200 20"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill="rgba(255, 255, 255, 0.2)"
              d="M0,10 C30,20 70,0 100,10 C130,20 170,0 200,10 L200,20 L0,20 Z"
            />
            <path
              fill="rgba(255, 255, 255, 0.15)"
              d="M0,15 C40,5 60,15 100,10 C140,5 160,15 200,10 L200,20 L0,20 Z"
            />
          </svg>
        </div>
      </div>

      {/* Right side signup form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            <img src={Logo} alt="vital Source" className="h-10 mx-auto" />
          </div>

          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-800">Create Account</h2>
            <p className="text-gray-600 mt-2">
              Join our shopping community today
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 block">
                  First Name
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg
                      className="h-5 w-5 text-gray-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className="pl-10 w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#f36812] focus:border-transparent bg-white shadow-sm"
                    placeholder="First name"
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 block">
                  Last Name
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg
                      className="h-5 w-5 text-gray-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    className="pl-10 w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#f36812] focus:border-transparent bg-white shadow-sm"
                    placeholder="Last name"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 block">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg
                    className="h-5 w-5 text-gray-400"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                </div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="pl-10 w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#f36812] focus:border-transparent bg-white shadow-sm"
                  placeholder="you@example.com"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 block">
                Phone Number
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg
                    className="h-5 w-5 text-gray-400"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                  </svg>
                </div>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="pl-10 w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#f36812] focus:border-transparent bg-white shadow-sm"
                  placeholder="03XXXXXXXXX"
                  pattern="03[0-9]{9}"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 block">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type={passwordVisible ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="pl-10 w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#f36812] focus:border-transparent bg-white shadow-sm"
                  placeholder="Create a strong password"
                  required
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <button
                    type="button"
                    onClick={generatePassword}
                    className="mr-2 text-gray-500 hover:text-gray-700"
                    title="Generate Password"
                  >
                    <RefreshCcw className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setPasswordVisible(!passwordVisible)}
                    className="text-gray-500 hover:text-gray-700"
                    title={passwordVisible ? "Hide Password" : "Show Password"}
                  >
                    {passwordVisible ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
              {/* Password Strength Indicator */}
              <div className="flex h-1.5 space-x-1 mt-1">
                {[1, 2, 3, 4].map((level) => (
                  <div
                    key={level}
                    className={`flex-1 rounded-full ${passwordStrength >= level
                      ? level <= 2
                        ? "bg-red-500"
                        : level <= 3
                          ? "bg-yellow-500"
                          : "bg-green-500"
                      : "bg-gray-300"
                      }`}
                  ></div>
                ))}
              </div>
              {passwordErrors.length > 0 && (
                <div className="text-xs text-red-600 mt-1">
                  {passwordErrors.map((error, index) => (
                    <p key={index}>• {error}</p>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 block">
                Confirm Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type={confirmPasswordVisible ? "text" : "password"}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="pl-10 w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#f36812] focus:border-transparent bg-white shadow-sm"
                  placeholder="Confirm your password"
                  required
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <button
                    type="button"
                    onClick={() =>
                      setConfirmPasswordVisible(!confirmPasswordVisible)
                    }
                    className="text-gray-500 hover:text-gray-700"
                    title={
                      confirmPasswordVisible ? "Hide Password" : "Show Password"
                    }
                  >
                    {confirmPasswordVisible ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
              {formData.password !== formData.confirmPassword &&
                formData.confirmPassword && (
                  <p className="text-xs text-red-600 mt-1">
                    Passwords do not match
                  </p>
                )}
            </div>

            <div className="flex items-center">
              <input
                id="agree-terms"
                name="agree-terms"
                type="checkbox"
                checked={agreeTerms}
                onChange={() => setAgreeTerms(!agreeTerms)}
                className="h-4 w-4 text-[#f36812] focus:ring-[#f36812] border-gray-300 rounded"
                required
              />
              <label
                htmlFor="agree-terms"
                className="ml-2 block text-sm text-gray-700"
              >
                I agree to the{" "}
                <Link
                  to="/terms-conditions" // Jahan aapko user ko bhejna hai
                  className="text-[#f36812] hover:text-[#d95700] font-medium"
                >
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link
                  to="/privacy-policy" // Privacy policy ka route path
                  className="text-[#f36812] hover:text-[#d95700] font-medium"
                >
                  Privacy Policy
                </Link>
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-md text-sm font-medium text-white bg-gradient-to-r from-[#f36812] to-[#ff8a43] hover:from-[#d95700] hover:to-[#e67a33] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#f36812] transition duration-200 disabled:opacity-70"
            >
              {loading ? (
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              ) : null}
              <span>
                {loading ? "Creating Account..." : "Create Your Account"}
              </span>
            </button>
          </form>

          {/* <div className="mt-8">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-gray-100 text-gray-500">Or sign up with</span>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <div>
                <a
                  href="#"
                  className="w-full inline-flex justify-center py-2.5 px-4 border border-gray-300 rounded-lg shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition duration-200"
                >
                  <img
                    className="h-5 w-5"
                    src="https://www.svgrepo.com/show/475656/google-color.svg"
                    alt="Google"
                  />
                  <span className="ml-2">Google</span>
                </a>
              </div>

              <div>
                <a
                  href="#"
                  className="w-full inline-flex justify-center py-2.5 px-4 border border-gray-300 rounded-lg shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition duration-200"
                >
                  <img
                    className="h-5 w-5"
                    src="https://www.svgrepo.com/show/512317/github-142.svg"
                    alt="GitHub"
                  />
                  <span className="ml-2">GitHub</span>
                </a>
              </div>
            </div>
          </div> */}

          <p className="mt-8 text-center text-sm text-gray-600">
            Already have an account?{" "}
            <Link
              to="/login"
              className="font-medium text-[#f36812] hover:text-[#d95700]"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>
      <Toaster />
    </div>
  );
};

export default Signup;
